/**
 * Pointer-lock binding for the desktop lobby. ecctrl's `useFollowCam` already
 * checks `document.pointerLockElement || isMouseDown` (see ecctrl
 * src/hooks/useFollowCam.tsx:62), so once the document is in pointer lock the
 * camera rotates from raw mouse movement without needing a drag — fixing the
 * "cursor leaves the window, camera stops turning" footgun.
 *
 * Two halves:
 *   - <PointerLockBinder/>  mounts INSIDE the Canvas; clicks on the canvas
 *     element request pointer lock.
 *   - <PointerLockHint/>    mounts as a DOM sibling of the Canvas; shows a
 *     "Click to play" overlay whenever pointer lock is not active. Hidden on
 *     touch devices (joystick path).
 *
 * On Esc / pointer-lock-loss we also dispatch synthetic keyups for every
 * movement key. drei's <KeyboardControls> listens at window level and has no
 * pointer-lock gate of its own, so without this a held W/A/S/D/Arrow/Space/
 * Shift keeps driving ecctrl after the user has released the cursor.
 *
 * Skip a chat-input focus check too — when the user is typing, clicking the
 * canvas elsewhere should still grab focus, but typing itself should never
 * trigger lock acquisition.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { useThree } from "@react-three/fiber";

import { useIsTouch } from "../util/useIsTouch";
import { useStore } from "../store";

const MOVEMENT_KEY_CODES = [
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
  "ShiftLeft",
  "ShiftRight",
];

export function PointerLockBinder(): null {
  const gl = useThree((s) => s.gl);
  useEffect(() => {
    const el = gl.domElement;
    const onClick = (): void => {
      if (document.pointerLockElement === el) return;
      // Don't grab the cursor while the admin aerial/stream camera is on — the
      // follow cam is disabled, so locking would just hide the cursor and trap
      // the streamer with no way to click the toggle off.
      if (useStore.getState().aerialView) return;
      const active = document.activeElement;
      if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
      el.requestPointerLock?.();
    };
    // Block ecctrl's drag-rotate path. ecctrl's useFollowCam latches
    // `isMouseDown` on `mousedown` and only clears on `mouseup`; the click
    // that requests pointer lock fires mousedown, and if the matching mouseup
    // is eaten by the lock transition the camera stays armed and drifts on
    // Esc-release. Stop the mousedown/mouseup events from reaching ecctrl
    // entirely — pointer lock is the only camera mode we want.
    const swallow = (e: Event): void => e.stopImmediatePropagation();

    // Defensive mousemove swallow when the cursor is free. ecctrl's gate
    // (`pointerLockElement || isMouseDown`) should already block rotation,
    // but if anything ever leaks (focus quirks, browser bugs) the canvas
    // simply won't see mousemove until lock is reacquired.
    const swallowMouseMoveWhenUnlocked = (e: MouseEvent): void => {
      if (!document.pointerLockElement) e.stopImmediatePropagation();
    };

    // When pointer lock exits (Esc / programmatic release / tab blur), clear
    // any held movement keys so the avatar stops immediately.
    const releaseHeldKeys = (): void => {
      for (const code of MOVEMENT_KEY_CODES) {
        window.dispatchEvent(
          new KeyboardEvent("keyup", {
            key: code.startsWith("Key") ? code.slice(3).toLowerCase() : code,
            code,
            bubbles: true,
          }),
        );
      }
    };

    const onPointerLockChange = (): void => {
      if (!document.pointerLockElement) releaseHeldKeys();
    };

    // Window blur also strands held keys (alt-tab away mid-sprint).
    const onWindowBlur = (): void => releaseHeldKeys();

    el.addEventListener("click", onClick);
    el.addEventListener("mousedown", swallow, true);
    el.addEventListener("mouseup", swallow, true);
    el.addEventListener("mousemove", swallowMouseMoveWhenUnlocked, true);
    document.addEventListener("pointerlockchange", onPointerLockChange);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      el.removeEventListener("click", onClick);
      el.removeEventListener("mousedown", swallow, true);
      el.removeEventListener("mouseup", swallow, true);
      el.removeEventListener("mousemove", swallowMouseMoveWhenUnlocked, true);
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [gl]);
  return null;
}

export function PointerLockHint(): JSX.Element | null {
  const isTouch = useIsTouch();
  const [locked, setLocked] = useState<boolean>(
    typeof document !== "undefined" ? !!document.pointerLockElement : false,
  );
  useEffect(() => {
    const onChange = (): void => setLocked(!!document.pointerLockElement);
    document.addEventListener("pointerlockchange", onChange);
    return () => document.removeEventListener("pointerlockchange", onChange);
  }, []);
  if (isTouch || locked) return null;
  const key: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 22,
    height: 22,
    padding: "0 6px",
    margin: "0 2px",
    borderRadius: 5,
    background: "#2a2a36",
    border: "1px solid #3a3a48",
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
  };
  const fkey: CSSProperties = {
    ...key,
    background: "#ffd86b",
    borderColor: "#ffd86b",
    color: "#1a1606",
  };
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "16px 22px",
        background: "rgba(16,16,24,0.9)",
        border: "1px solid #2a2a36",
        borderRadius: 12,
        color: "#e8e8ef",
        textAlign: "center",
        pointerEvents: "none",
        zIndex: 10,
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
        Click to play
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.9, color: "#c8c8d4" }}>
        <span style={key}>W</span>
        <span style={key}>A</span>
        <span style={key}>S</span>
        <span style={key}>D</span> move
        <span style={{ margin: "0 8px", color: "#555" }}>·</span>
        <span style={key}>Space</span> jump
        <br />
        Walk to the throne and press <span style={fkey}>F</span>
        <b style={{ color: "#ffd86b" }}> to claim it</b>
      </div>
      <div style={{ fontSize: 12, color: "#888", marginTop: 10 }}>
        Esc to release cursor
      </div>
    </div>
  );
}
